import { css } from '@styled-system/css';
import type { FC, ReactNode } from 'react';
import { useState } from 'react';

import { AuthGuard } from '@/features/auth/components';
import { MainSidebar } from '@/shared/components/layout/MainSidebar';
import { MobileHeader } from '@/shared/components/layout/MobileHeader';
import { useIsMobile } from '@/shared/hooks/useIsMobile';

interface AppLayoutProps {
  children: ReactNode;
  className?: string;
}

export const AppLayout: FC<AppLayoutProps> = ({ children, className }) => {
  const isMobile = useIsMobile();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const layoutStyles = css({
    display: 'flex',
    flexDirection: isMobile ? 'column' : 'row',
    h: '100vh',
    bg: 'grey.950',
    overflow: 'hidden',
  });

  const contentStyles = css({
    flex: 1,
    display: 'flex',
    overflow: 'hidden',
  });

  const mainContentStyles = css({
    flex: 1,
    bg: 'grey.950',
    overflow: 'auto',
  });

  const overlayStyles = css({
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    bg: 'rgba(0, 0, 0, 0.5)',
    zIndex: 40,
    animation: 'fadeIn 0.3s ease-out',
  });

  const handleMenuClick = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const handleOverlayClick = () => {
    setIsSidebarOpen(false);
  };

  return (
    <AuthGuard>
      <div className={`${layoutStyles} ${className || ''}`}>
        {isMobile && <MobileHeader onMenuClick={handleMenuClick} />}

        {isMobile && isSidebarOpen && <div className={overlayStyles} onClick={handleOverlayClick} />}

        {(!isMobile || isSidebarOpen) && <MainSidebar onClose={isMobile ? handleOverlayClick : undefined} />}

        <div className={contentStyles}>
          <main className={mainContentStyles}>{children}</main>
        </div>
      </div>
    </AuthGuard>
  );
};
